import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCAGStore } from '../../stores/cagStore';
import { useGameStore } from '../../stores/gameStore';
import { useAuthStore } from '../../stores/authStore';

export const CAGChat: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { user, token } = useAuthStore();
  const { selectedGame } = useGameStore();
  const {
    currentConversation,
    messages,
    conversationStats,
    isSendingMessage,
    error,
    sendMessage,
    loadStats,
    clearError,
    resetConversation
  } = useCAGStore();

  const [query, setQuery] = useState('');
  const [showStats, setShowStats] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load stats when component mounts or conversation changes
    if (currentConversation) {
      loadStats(token || undefined);
    }
  }, [currentConversation, loadStats, token]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isSendingMessage || !currentConversation) return;

    try {
      await sendMessage(query, token || undefined);
      setQuery('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleNewConversation = () => {
    resetConversation();
    // Navigate back to game selection
    window.location.href = '/games';
  };

  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>🔐 Authentication Required</h2>
        <p>CAG mode requires you to be logged in for conversation management.</p>
        <Link to="/login" style={{ color: '#0066cc', textDecoration: 'none' }}>
          Please log in to continue
        </Link>
      </div>
    );
  }

  if (!currentConversation) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>❌ No Active Conversation</h2>
        <p>No CAG conversation found. Please select a game to start a new conversation.</p>
        <Link to="/games" style={{ color: '#0066cc', textDecoration: 'none' }}>
          Back to Game Selection
        </Link>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh' }}>
      {/* Header */}
      <div style={{ 
        padding: '15px 20px', 
        backgroundColor: '#28a745',
        color: 'white',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px' }}>
            🚀 {selectedGame?.name || gameId} - CAG Mode
          </h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
            Optimized conversation with smart context management
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowStats(!showStats)}
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            📊 {showStats ? 'Hide' : 'Show'} Stats
          </button>
          <button
            onClick={handleNewConversation}
            style={{
              padding: '8px 12px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🆕 New Chat
          </button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && conversationStats && (
        <div style={{ 
          padding: '10px 20px',
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          fontSize: '12px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            <div><strong>Exchanges:</strong> {conversationStats.totalExchanges}</div>
            <div><strong>Tokens Used:</strong> {conversationStats.totalTokensUsed}</div>
            <div><strong>Rules Loaded:</strong> {conversationStats.rulesLoaded}</div>
            <div><strong>Avg Tokens/Exchange:</strong> {Math.round(conversationStats.avgTokensPerExchange)}</div>
            <div><strong>Age:</strong> {Math.round(conversationStats.conversationAge)}m</div>
            <div><strong>Optimizations:</strong> {conversationStats.contextOptimizations}</div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{ 
          padding: '10px 20px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          borderBottom: '1px solid #f5c6cb'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>❌ {error}</span>
            <button
              onClick={clearError}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#721c24',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
      }}>
        {messages.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            marginTop: '50px' 
          }}>
            <h3>Welcome to CAG Mode! 🚀</h3>
            <p>Ask your first question about {selectedGame?.name || gameId}</p>
            <p style={{ fontSize: '14px', marginTop: '20px' }}>
              <strong>CAG Benefits:</strong><br />
              • Smart rule selection based on your question<br />
              • Optimized token usage for better performance<br />
              • Context-aware responses with relevant sources<br />
              • Conversation history management
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '70%',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: message.role === 'user' ? '#0066cc' : '#f0f0f0',
                  color: message.role === 'user' ? 'white' : 'black',
                }}
              >
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {message.content}
                </div>
                
                {/* Show sources and stats for assistant messages */}
                {message.role === 'assistant' && (message.sources || message.tokenUsage) && (
                  <div style={{ 
                    marginTop: '10px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid #ddd',
                    fontSize: '11px',
                    color: '#666'
                  }}>
                    {message.sources && message.sources.length > 0 && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>📚 Sources:</strong> {message.sources.slice(0, 3).join(', ')}
                        {message.sources.length > 3 && ` (+${message.sources.length - 3} more)`}
                      </div>
                    )}
                    {message.tokenUsage && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>⚡ Tokens:</strong> {message.tokenUsage.total} 
                        ({message.tokenUsage.prompt} prompt + {message.tokenUsage.completion} response)
                      </div>
                    )}
                    {message.optimizationStats && (
                      <div>
                        <strong>🎯 Optimization:</strong> {message.optimizationStats.rulesLoaded} rules, 
                        {message.optimizationStats.historyLength} context messages
                      </div>
                    )}
                  </div>
                )}
                
                <div style={{ 
                  fontSize: '10px', 
                  marginTop: '5px', 
                  opacity: 0.7 
                }}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form onSubmit={handleSendMessage} style={{ 
        padding: '20px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: 'white'
      }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Ask a question about ${selectedGame?.name || gameId}...`}
            disabled={isSendingMessage}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
          <button
            type="submit"
            disabled={isSendingMessage || !query.trim()}
            style={{
              padding: '12px 24px',
              backgroundColor: isSendingMessage ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isSendingMessage ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {isSendingMessage ? '🔄 Sending...' : '🚀 Send'}
          </button>
        </div>
      </form>
    </div>
  );
};